import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { UploadBatchStatus } from '@ecom/contracts';
import { useIngestionStore } from '@/stores/ingestion.store';
import { AsyncSelect } from '@/components/async-select';
import { mappingProfileOptions } from '@/lib/options';
import { useUiStore } from '@/i18n';
import { MESSAGES, type MessageKey } from '@/i18n/messages';
import { useT } from '@/i18n/use-t';
import { count, dateTime } from '@/lib/format';
import { BoxIcon, CheckIcon, ChevronRightIcon, TrashIcon, UploadIcon } from '@/components/icons';
import { Choices, TextField } from '@/components/fields';
import { Page } from '@/components/page';
import {
  Alert,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  EmptyState,
  IconButton,
  Pill,
  Select,
  cx,
  type PillTone,
} from '@/components/ui';

/**
 * Bulk import (ADR-014).
 *
 * The API's four steps are the screen's four steps, on purpose: upload, map,
 * dry run, import. An operator sees exactly which rows would fail *before*
 * anything reaches the catalogue, which is the whole reason the pipeline is
 * four calls instead of one.
 *
 * The steps sit under the header; the current step's card runs to the bottom
 * of the window with its buttons at its foot, so the way forward is always in
 * the same place.
 */
export function ImportPage() {
  const {
    batch,
    validation,
    fields,
    target,
    setTarget,
    history,
    columnMap,
    step,
    busy,
    error,
    loadReference,
    upload,
    setColumn,
    applyProfile,
    deleteProfile,
    validate,
    run,
    cancel,
    saveProfile,
    reset,
    watch,
    downloading,
    downloadErrorReport,
  } = useIngestionStore();

  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const fileInput = useRef<HTMLInputElement>(null);
  const [profileName, setProfileName] = useState('');
  // The saved mapping picked for this file — the one "delete" would remove.
  // Tied to the batch, so a new file starts with none picked.
  const [picked, setPicked] = useState<{ batchId: string; id: string; name: string } | null>(null);
  const profile = picked && picked.batchId === batch?.id ? picked : null;
  const [confirmDelete, setConfirmDelete] = useState(false);

  /** A field in words; one the API adds later shows its code rather than nothing. */
  const fieldLabel = (field: string) => {
    const key = `importField_${field}`;
    return key in MESSAGES ? t(key as MessageKey) : field;
  };

  // "Import" on the customers list opens this on customers (ADR-070).
  const [params] = useSearchParams();
  const asked = params.get('target');
  useEffect(() => {
    if (asked === 'CUSTOMERS' || asked === 'PRODUCTS') void setTarget(asked);
    void loadReference();
  }, [asked, loadReference, setTarget]);

  // The progress stream lives exactly as long as the running step.
  useEffect(() => {
    if (step !== 'running') return;
    return watch();
  }, [step, watch]);

  const steps: { id: typeof step; label: string }[] = [
    { id: 'upload', label: t('stepUpload') },
    { id: 'map', label: t('stepMap') },
    { id: 'review', label: t('stepReview') },
    { id: 'done', label: t('stepImport') },
  ];
  const currentIndex = steps.findIndex((entry) => entry.id === (step === 'running' ? 'done' : step));
  const report =
    batch?.hasErrorReport ? (
      <Button
        variant="secondary"
        onClick={() => void downloadErrorReport(batch.id)}
        disabled={downloading !== null}
      >
        {downloading === batch.id ? t('loading') : t('downloadErrors')}
      </Button>
    ) : null;

  return (
    <Page
      title={t('import')}
      subtitle={t('importSubtitle')}
      actions={
        batch ? (
          <Button variant="secondary" onClick={reset}>
            {t('startOver')}
          </Button>
        ) : null
      }
      width="wide"
    >
      {/* The steps are the API's steps; showing them is showing the guarantee. */}
      <Card className="px-5 py-4">
        <ol className="flex items-center gap-3 overflow-x-auto [scrollbar-width:none]">
          {steps.map((entry, index) => {
            const done = index < currentIndex;
            const current = index === currentIndex;

            return (
              <li
                key={entry.id}
                aria-current={current ? 'step' : undefined}
                className="flex flex-1 items-center gap-2.5 last:flex-none"
              >
                <span
                  className={cx(
                    'flex size-7 shrink-0 items-center justify-center rounded-full border text-xs font-semibold',
                    done && 'border-success bg-success text-success-ink',
                    current && 'border-brand-600 bg-brand-600 text-brand-ink',
                    !done && !current && 'border-line text-ink-faint',
                  )}
                >
                  {done ? (
                    <CheckIcon className="size-3.5" />
                  ) : (
                    <span data-numeric dir="ltr">
                      {index + 1}
                    </span>
                  )}
                </span>
                <span
                  className={cx(
                    'text-sm whitespace-nowrap',
                    current ? 'font-medium text-ink' : done ? 'text-ink' : 'text-ink-faint',
                  )}
                >
                  {entry.label}
                </span>
                {index < steps.length - 1 ? (
                  <span aria-hidden className={cx('h-px min-w-6 flex-1', done ? 'bg-success' : 'bg-line')} />
                ) : null}
              </li>
            );
          })}
        </ol>
      </Card>

      {error ? <Alert>{error}</Alert> : null}

      {step === 'upload' ? (
        <Card>
          {/* What the file holds (ADR-070): the same four steps for either. */}
          <div className="px-5 pt-5">
            <Choices
              legend={t('importTarget')}
              name="import-target"
              columns={2}
              options={[
                { value: 'PRODUCTS', label: t('importTargetProducts'), hint: t('importTargetProductsHint') },
                { value: 'CUSTOMERS', label: t('importTargetCustomers'), hint: t('importTargetCustomersHint') },
              ]}
              value={target}
              onChange={(next) => void setTarget(next)}
            />
          </div>
          <div className="m-5 flex flex-col items-center gap-4 rounded-card border border-dashed border-line px-6 py-10 text-center">
            <UploadIcon className="size-8 text-ink-faint" />
            <div>
              <p className="font-medium text-ink">{t('chooseFile')}</p>
              <p className="mt-1 text-sm text-ink-muted">{target === 'CUSTOMERS' ? t('chooseCustomersFileHint') : t('chooseFileHint')}</p>
            </div>

            <input
              ref={fileInput}
              type="file"
              accept=".csv,.xlsx,text/csv"
              className="sr-only"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void upload(file);
              }}
            />
            <Button onClick={() => fileInput.current?.click()} disabled={busy}>
              {busy ? t('loading') : t('chooseFile')}
            </Button>
          </div>
        </Card>
      ) : null}

      {step === 'map' && batch ? (
        <Card className="flex flex-1 flex-col">
          <CardHeader
            title={t('mapColumns')}
            subtitle={batch.targetType === 'CUSTOMERS' ? t('mapCustomerColumnsHint') : t('mapColumnsHint')}
            action={
              // Saved mappings are for the supplier who sends the same file monthly — products only (ADR-070).
              batch.targetType === 'CUSTOMERS' ? undefined : (
              <div className="flex items-center gap-2">
                <div className="w-56">
                  <AsyncSelect
                    id="saved-mapping"
                    value={profile?.id ?? ''}
                    allowClear={false}
                    emptyLabel={t('savedMapping')}
                    fetchPage={mappingProfileOptions.page}
                    onChange={(profileId, option) => {
                      if (!profileId) return;
                      setPicked({ batchId: batch.id, id: profileId, name: option?.label ?? '' });
                      void applyProfile(profileId, option?.label);
                    }}
                  />
                </div>
                {profile ? (
                  <IconButton
                    label={t('deleteSavedMapping')}
                    onClick={() => setConfirmDelete(true)}
                    disabled={busy}
                    className="shrink-0 hover:text-danger"
                  >
                    <TrashIcon className="size-4" />
                  </IconButton>
                ) : null}
              </div>
              )
            }
          />

          {batch.source ? (
            // Read in its platform's shape (ADR-091): the mapping below is the preset's, to check rather than write.
            <div className="px-5 pb-3">
              <Alert tone="info">{t('importReadAs', { source: t(`importSource_${batch.source}`) })}</Alert>
            </div>
          ) : null}

          <ul className="flex flex-col border-t border-line">
            {batch.detectedColumns.map((column) => (
              <li key={column} className="flex flex-wrap items-center gap-3 border-b border-line px-5 py-3">
                {/* The file's own header text — shown as it was written, Arabic or not. */}
                <span dir="auto" className="min-w-0 flex-1 truncate font-mono text-xs text-ink">
                  {column}
                </span>
                <ChevronRightIcon className="size-4 shrink-0 text-ink-faint rtl:-scale-x-100" />
                <Select
                  value={columnMap[column] ?? ''}
                  aria-label={column}
                  onChange={(event) => setColumn(column, event.target.value)}
                  className="min-w-52"
                >
                  <option value="">{t('ignoreColumn')}</option>
                  {fields.map((field) => (
                    <option key={field} value={field}>
                      {fieldLabel(field)}
                    </option>
                  ))}
                </Select>
              </li>
            ))}
          </ul>

          <StepFooter>
            {batch.targetType === 'CUSTOMERS' ? (
              <span className="me-auto" />
            ) : (
              <>
                <div className="me-auto w-56">
                  <TextField
                    label={t('saveMapping')}
                    hint={t('saveMappingHint')}
                    value={profileName}
                    onChange={setProfileName}
                  />
                </div>
                <Button
                  variant="secondary"
                  disabled={busy || !profileName.trim()}
                  onClick={async () => {
                    if (await saveProfile(profileName.trim())) setProfileName('');
                  }}
                >
                  {t('save')}
                </Button>
              </>
            )}
            <Button onClick={() => void validate()} disabled={busy}>
              {busy ? t('loading') : t('runDryRun')}
            </Button>
          </StepFooter>
        </Card>
      ) : null}

      {step === 'review' && batch && validation ? (
        <Card className="flex flex-1 flex-col">
          <CardHeader title={t('dryRunResult')} subtitle={t('dryRunHint')} />

          <div className="grid gap-4 border-t border-line p-5 sm:grid-cols-3">
            <Stat label={t('rowsTotal')} value={count(batch.totalRows, locale)} />
            <Stat label={t('rowsOk')} value={count(validation.validRows, locale)} tone="success" />
            <Stat
              label={t('rowsFailed')}
              value={count(validation.invalidRows, locale)}
              tone={validation.invalidRows > 0 ? 'danger' : 'neutral'}
            />
          </div>

          {validation.preview.length > 0 ? (
            <div className="overflow-x-auto border-t border-line">
              <p className="px-5 pt-4 text-xs font-medium text-ink">{t('preview')}</p>
              {/* The final eyeball check: how the first rows were actually read. */}
              <table className="mt-2 w-full text-xs">
                <thead>
                  <tr className="text-ink-faint">
                    {Object.keys(validation.preview[0]).map((column) => (
                      <th key={column} dir="ltr" scope="col" className="px-5 py-2 text-start font-medium">
                        {column}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {validation.preview.slice(0, 5).map((row, index) => (
                    <tr key={index} className="border-t border-line">
                      {Object.keys(validation.preview[0]).map((column) => (
                        <td key={column} className="max-w-40 truncate px-5 py-2 text-ink">
                          {row[column]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {validation.errors.length > 0 ? (
            <div className="border-t border-line">
              <p className="px-5 pt-4 text-xs font-medium text-ink">{t('errorSample')}</p>
              <ul className="flex flex-col p-5 pt-2">
                {validation.errors.slice(0, 8).map((row, index) => (
                  <li key={index} className="border-b border-line py-2 text-sm last:border-0">
                    <span data-numeric dir="ltr" className="me-2 text-xs text-ink-muted">
                      {t('row')} {row.row}
                    </span>
                    <span className="text-danger">{row.message}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <StepFooter>
            {report}
            <Button variant="secondary" onClick={() => useIngestionStore.setState({ step: 'map' })}>
              {t('backToMapping')}
            </Button>
            <Button onClick={() => void run()} disabled={busy || validation.validRows === 0}>
              {/* Importing zero good rows is not a thing worth offering. */}
              {t('importRows', { count: count(validation.validRows, locale) })}
            </Button>
          </StepFooter>
        </Card>
      ) : null}

      {(step === 'running' || step === 'done') && batch ? (
        <Card className="flex flex-1 flex-col">
          <CardHeader title={step === 'running' ? t('importing') : t('importFinished')} />

          <div className="border-t border-line p-5">
            <div className="mb-2 flex items-baseline justify-between text-sm">
              <span className="text-ink-muted">
                {count(batch.processedRows, locale)} / {count(batch.totalRows, locale)}
              </span>
              <span data-numeric className="font-medium text-ink">
                {count(batch.percent, locale)}%
              </span>
            </div>
            <div
              role="progressbar"
              aria-valuenow={batch.percent}
              aria-valuemin={0}
              aria-valuemax={100}
              className="h-2 w-full overflow-hidden rounded-full bg-surface-sunken"
            >
              <div
                className="h-full rounded-full bg-brand-600 transition-all"
                style={{ width: `${batch.percent}%` }}
              />
            </div>

            <div className="mt-5 grid gap-4 sm:grid-cols-3">
              <Stat label={t('rowsOk')} value={count(batch.succeededRows, locale)} tone="success" />
              <Stat label={t('rowsFailed')} value={count(batch.failedRows, locale)} tone={batch.failedRows > 0 ? 'danger' : 'neutral'} />
              <Stat label={t('status')} value={t(statusKey(batch.status))} />
            </div>

            {batch.failureReason ? (
              <div className="mt-4">
                <Alert>{batch.failureReason}</Alert>
              </div>
            ) : null}
          </div>

          <StepFooter>
            {step === 'running' ? (
              <Button variant="secondary" onClick={() => void cancel()}>
                {t('cancel')}
              </Button>
            ) : (
              <>
                {report}
                <Button onClick={reset}>{t('importAnother')}</Button>
              </>
            )}
          </StepFooter>
        </Card>
      ) : null}

      {step === 'upload' ? (
        <Card className="flex flex-1 flex-col">
          <CardHeader title={t('importHistory')} />

          <div className="flex flex-1 flex-col border-t border-line">
            {history.length === 0 ? (
              <EmptyState title={t('noImports')} icon={<BoxIcon />} />
            ) : (
              <ul className="flex flex-col">
                {history.map((entry) => (
                  <li key={entry.id} className="flex flex-wrap items-center gap-3 border-b border-line px-5 py-3 last:border-0">
                    <span dir="ltr" className="min-w-0 flex-1 truncate text-sm text-ink">
                      {entry.fileName}
                    </span>
                    {/* Which platform's export it was (ADR-091). */}
                    {entry.source ? <Pill>{t(`importSource_${entry.source}`)}</Pill> : null}
                    <Pill tone={batchTone(entry.status)}>{t(statusKey(entry.status))}</Pill>
                    <span data-numeric className="text-xs text-ink-muted">
                      {count(entry.succeededRows, locale)} / {count(entry.totalRows, locale)}
                    </span>
                    <span className="text-xs text-ink-muted">{dateTime(entry.createdAt, locale)}</span>
                    {/* A past file's rejected rows, to fix and send again — not only the one just run. */}
                    {entry.hasErrorReport ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => void downloadErrorReport(entry.id)}
                        disabled={downloading !== null}
                      >
                        {downloading === entry.id ? t('loading') : t('importRowErrors')}
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>
      ) : null}

      <ConfirmDialog
        open={confirmDelete && profile !== null}
        title={t('deleteSavedMappingTitle', { name: profile?.name ?? '' })}
        body={t('deleteSavedMappingBody')}
        confirmLabel={t('deleteMapping')}
        cancelLabel={t('cancel')}
        busy={busy}
        onConfirm={async () => {
          if (profile && (await deleteProfile(profile.id))) setPicked(null);
          setConfirmDelete(false);
        }}
        onCancel={() => setConfirmDelete(false)}
      />
    </Page>
  );
}

/** A step's buttons, at the foot of its card — primary last, at the far edge. */
function StepFooter({ children }: { children: ReactNode }) {
  return (
    <div className="mt-auto flex flex-wrap items-end justify-end gap-2 border-t border-line px-5 py-4">{children}</div>
  );
}

function Stat({ label, value, tone = 'neutral' }: { label: string; value: string; tone?: 'neutral' | 'success' | 'danger' }) {
  return (
    <div className="rounded-control border border-line p-3">
      <p className="text-xs text-ink-muted">{label}</p>
      <p
        data-numeric
        className={cx(
          'mt-1 text-lg font-semibold',
          tone === 'success' && 'text-success',
          tone === 'danger' && 'text-danger',
          tone === 'neutral' && 'text-ink',
        )}
      >
        {value}
      </p>
    </div>
  );
}

/** A batch's status, in words. A status the API adds later renders as its key, not a blank. */
function statusKey(status: UploadBatchStatus | string) {
  return `importStatus_${status}` as `importStatus_${UploadBatchStatus}`;
}

function batchTone(status: string): PillTone {
  if (status === 'DONE') return 'success';
  if (status === 'FAILED' || status === 'CANCELLED') return 'danger';
  if (status === 'RUNNING' || status === 'VALIDATING') return 'info';
  return 'neutral';
}
